import React, { useMemo } from 'react';
import { Col, Row, Card } from 'tea-component';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import { analysisPlatformTopInfo, getStoreRankTopName } from '@/pages/intelligence/_config/storeRank/utils';
import { changeMulRankTitle } from '../../utils';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';

import type { IBaseProps, IDateTypely, IGameType } from '@/pages/intelligence/types/common';

import "./index.scss"

const formatLineData = (lineData: ILineData[], cfg: { isRangeDayMonth: boolean; }): ILineData[] => {
  // console.log('222lineData: ', lineData);
  if (!Array.isArray(lineData)) return [];

  const { isRangeDayMonth } = cfg || {};
  const dateKey = isRangeDayMonth ? 'launch_date' : 'time';
  return lineData.map((lineDataItem) => {
    const histories = lineDataItem?.history?.map((history: any) => {
      return {
        time: history?.[dateKey],
        value: history?.value,
      };
    });

    return {
      id: lineDataItem?.game_id,
      name: lineDataItem?.name,
      cover: '',
      history: histories || [],
    };
  });
};

type IPlatformTopInfos = {
  platform: string,
  platformIcon: string,
  top: string,
  topName: string,
};

type ILineData = any;

type ILineDatas = {
  source: string;
  games: ILineData[];
};

interface IVisualisItemProps extends IBaseProps {
  entityType: IGameType;
  chartType?: ChartType;
  topChartsType: string;
  dateType: IDateTypely;
  isMultiple: boolean;
  isRangeDayMonth: boolean;
  isStepLine?: boolean;
  lineData: ILineData[];
}

interface IVisualisListProps extends Omit<IVisualisItemProps, 'lineData'> {
  lineDatas: ILineDatas[];
}

interface IVisualisListWrapperProps extends IVisualisListProps {
  isLoading: boolean;
  countryType: 'single' | 'multiple';
  sources: string[];
  lineDatas: ILineDatas[];
}

const VisualisItem: React.FC<IVisualisItemProps> = (props) => {
  const {
    entityType = 'mobile',
    chartType = ChartType.Line,
    topChartsType,
    dateType,
    titleNow,
    isMultiple = true,
    isRangeDayMonth = false,
    isStepLine = false,
    lineData: lineDataOuter,
  } = props;

  const lineData = useMemo<{
    source: ILineData[];
  }>(() => {
    const formattedLineData = formatLineData(lineDataOuter, { isRangeDayMonth });
    return { source: formattedLineData };
  }, [isRangeDayMonth, lineDataOuter]);

  const isNoData = useMemo<boolean>(() => {
    if (!Array.isArray(lineData?.source)) return true;
    const hasData = lineData?.source?.some((item) => {
      return item?.history?.some((subItem: any) => {
        const metricValue = Number.parseFloat(subItem?.value);
        return subItem?.time && !Number.isNaN(metricValue);
      });
    });
    return !hasData;
  }, [lineData]);

  // 获取折线图hooks
  const {
    lineOption
  } = useLineCharts({
    lineData,
    dateType,
    topChartsType,
    imageType: entityType,
    titleNow,
    metric: 'source',
    isStep: isStepLine,
    gridConfig: {
      left: 30,
    },
    yAxisConfig: {
      minInterval: 1,
      inverse: !isMultiple,
      min: isMultiple ? undefined : 1,
    },
  });

  return (
    <div className="card-line">
      <ChartsIndex
        chartLoading={false}
        isNodata={isNoData}
        chartType={chartType}
        lineOption={lineOption}
      />
    </div>
  );
};

const VisualisList: React.FC<IVisualisListProps> = (props) => {
  const {
    entityType = 'mobile',
    chartType,
    topChartsType,
    dateType,
    isMultiple,
    isRangeDayMonth,
    isStepLine,
    lineDatas = [],
  } = props;

  const { formatMessage } = useIntlExtend();

  const renderLineChartTitle = (source: string, isMultiple: boolean) => {
    if (isMultiple) {
      const rankRangeText = changeMulRankTitle(source);
      return (
        <span>{formatMessage({ id: 'dbi_compare_mobile_number_of_countries_rank' }, { rankRangeText })}</span>
      );
    }

    const { platform, top } = analysisPlatformTopInfo(source);
    const platformIcon = getDataSourceIcon(platform);
    const topName = getStoreRankTopName(entityType, top, formatMessage);

    return (
      <>
        <img className="compare-echart-datasorce" src={platformIcon} />
        <span>{`${topName}${formatMessage({ id: 'dbi_composed_label_trends' })}`}</span>
      </>
    );
  };

  const colSpan = lineDatas?.length === 1 ? 24 : 12;

  return (
    <Card className="compare-storerank-echart-content">
      <Card.Body>
        <Row gap={20}>
          {lineDatas?.map((lineData) => (
            <Col className="overview-section" span={colSpan}>
              <div className='dbi-compare-echart-datasorce'>{renderLineChartTitle(lineData?.source, isMultiple)}</div>
              <VisualisItem
                entityType={entityType}
                chartType={chartType}
                topChartsType={topChartsType}
                dateType={dateType}
                isMultiple={isMultiple}
                isRangeDayMonth={isRangeDayMonth}
                isStepLine={isStepLine}
                lineData={lineData?.games}
              />
            </Col>
          ))}
        </Row>
      </Card.Body>
    </Card>
  );
};

const VisualisListWrapper: React.FC<IVisualisListWrapperProps> = (props) => {
  const { isLoading, countryType, sources, lineDatas, ...restProps } = props;

  const { locale, formatMessage } = useIntlExtend();

  const isMultiple = countryType === 'multiple';

  const platformTopInfos = useMemo<IPlatformTopInfos | null>(() => {
    if (!isMultiple) return null;
    const { platform, top } = analysisPlatformTopInfo(sources?.[0]);
    const platformIcon = getDataSourceIcon(platform);
    const topName = getStoreRankTopName('mobile', top, formatMessage);
    return {
      platform,
      platformIcon,
      top,
      topName,
    };
  }, [locale, isMultiple, countryType, sources]);

  return (
    <Abnormal
      isLoading={isLoading}
      isNoData={!isLoading && !lineDatas?.length}
      component={
        <div>
          {isMultiple && (
            <div style={{ marginLeft: 20, color: '#000', fontSize: 14, marginTop: 20 }} className="dbi-compare-echart-datasorce">
              <img className="compare-echart-datasorce" src={platformTopInfos?.platformIcon} />
              <span>{platformTopInfos?.topName}{formatMessage({ id: 'dbi_composed_label_trends' })}</span>
            </div>
          )}
          <VisualisList
            {...restProps}
            isMultiple={isMultiple}
            lineDatas={lineDatas}
          />
        </div>
      }
    />
  );
};

export default VisualisListWrapper;
